import React from 'react';
import { withLocalePrefix } from 'shared/utils/translation';
import BookmarkIcon from 'shared/components/birch/icons/bookmark';
import BookmarkFilledIcon from 'shared/components/birch/icons/bookmark_filled';
import { IconButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { standardRequestWithFlash } from 'shared/utils/request';
import { CustomIconTooltip } from '@grnhse/seedling/lib/birch/components/tooltips';
import { type Person } from 'talent_matching/types';
import { useUpdatePerson } from 'talent_matching/hooks/use_people';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { trackUserAction } from 'talent_matching/lib/tracker';

const t = withLocalePrefix('talent_matching');

const Bookmark = ({
  className,
  application,
  size,
  isFlat = true,
}: {
  className?: string;
  application: Person;
  size: 'sm' | 'md' | 'lg' | 'seedling-only-sm';
  isFlat?: boolean;
}) => {
  const updatePerson = useUpdatePerson();
  const { organizationId, userId, hiringPlanId } = useUserContext();

  const toggleBookmark = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    const nextBookmarked = !application.isBookmarked;

    // Optimistically update the UI
    updatePerson({ ...application, isBookmarked: nextBookmarked });

    try {
      if (application.isBookmarked) {
        await standardRequestWithFlash.delete('/bookmark', {
          data: { application_id: application.id },
        });
      } else {
        await standardRequestWithFlash.post('/bookmark', {
          application_id: application.id,
        });
      }

      // Track only after the mutation succeeds so a failed bookmark attempt
      // doesn't record a save/remove that never happened.
      trackUserAction({
        source: 'tm_app_review',
        organizationId,
        userId,
        hiringPlanId,
        category: 'action',
        action: 'save_for_later',
        applicationId: application.id,
        candidateId: application.personId,
        // Distinguishes saving vs. removing the bookmark.
        metadata: { bookmarked: nextBookmarked },
      });
    } catch {
      // Rollback UI update on error
      updatePerson({
        ...application,
        isBookmarked: application.isBookmarked,
      });
    }
  };

  const Icon = application.isBookmarked ? BookmarkFilledIcon : BookmarkIcon;
  const tooltip = application.isBookmarked
    ? t('bookmarks.tooltips.remove_bookmark')
    : t('bookmarks.tooltips.add_bookmark');

  return (
    <div className={'candidate-bookmark ' + (className || '')}>
      <CustomIconTooltip label={tooltip} ariaLabel={tooltip}>
        <IconButton
          icon={Icon}
          label={t('bookmark')}
          onClick={toggleBookmark}
          size={size}
          flat={isFlat}
        />
      </CustomIconTooltip>
    </div>
  );
};

export default Bookmark;
